import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ListChecks, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { PHASES } from "../../components/PhaseStepper";

const STATUSES = PHASES;

const REVIEW_TARGETS = [
  ["ffe", "FF&E"], ["budget", "Budget"], ["note", "Notes"], ["time", "Time"],
];

function ImportReviewSection({ project, onUpdate }) {
  const items = project.unclassified_items || [];
  const [choice, setChoice] = useState({});
  const [busyId, setBusyId] = useState(null);

  if (items.length === 0) return null;

  const fileItem = async (uid) => {
    const target = choice[uid];
    if (!target) { toast.error("Pick a tab to file this under."); return; }
    setBusyId(uid);
    try {
      const fd = new FormData();
      fd.append("target", target);
      await api.post(`/projects/${project.id}/unclassified/${uid}/file`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Filed to the right tab");
      onUpdate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not file item");
    } finally { setBusyId(null); }
  };

  const dismiss = async (uid) => {
    setBusyId(uid);
    try {
      await api.delete(`/projects/${project.id}/unclassified/${uid}`);
      onUpdate();
    } catch (err) {
      toast.error("Could not remove item");
    } finally { setBusyId(null); }
  };

  return (
    <div className="border border-[#E5D3C6] bg-[#FAF3EE] p-6" data-testid="import-review-section">
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle size={18} strokeWidth={1.5} className="text-[#B0724A]" />
        <h4 className="font-serif text-xl text-[#2B2622]">Needs review</h4>
        <span className="text-[11px] uppercase tracking-wider text-[#B0724A]">{items.length} imported</span>
      </div>
      <p className="text-[13px] text-[#8a5a3a] mb-4">
        These imported rows couldn't be auto-filed. Choose a tab for each, or remove it.
      </p>
      <div className="space-y-3">
        {items.map((u) => (
          <div key={u.id} className="bg-white border border-[#EBE5E0] p-3" data-testid={`review-item-${u.id}`}>
            <p className="text-sm text-[#2B2622] font-medium">{u.name || u.notes || "Untitled row"}</p>
            <p className="text-[11px] text-[#9b938b] mt-0.5">
              {[u.type_raw && `Type: ${u.type_raw}`, u.room, u.vendor, (u.amount || u.unit_price) && `$${u.amount || u.unit_price}`]
                .filter(Boolean).join(" · ") || "No extra details"}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <Select value={choice[u.id] || ""} onValueChange={(v) => setChoice({ ...choice, [u.id]: v })}>
                <SelectTrigger data-testid={`review-target-${u.id}`} className="h-9 w-40 rounded-none border-[#EBE5E0] focus:ring-0 text-xs">
                  <SelectValue placeholder="File under…" />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {REVIEW_TARGETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button data-testid={`review-file-${u.id}`} onClick={() => fileItem(u.id)} disabled={busyId === u.id}
                className="h-9 rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-[11px] px-4">
                File
              </Button>
              <Button data-testid={`review-dismiss-${u.id}`} variant="ghost" onClick={() => dismiss(u.id)} disabled={busyId === u.id}
                className="h-9 rounded-none text-[#9b938b] hover:text-[#B0724A] uppercase tracking-wider text-[11px] px-2">
                Remove
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChecklistSummary({ projectId }) {
  const [lists, setLists] = useState([]);
  useEffect(() => {
    api.get(`/projects/${projectId}/checklists`).then((r) => setLists(r.data)).catch(() => {});
  }, [projectId]);

  if (lists.length === 0) return null;
  const all = lists.flatMap((l) => l.items || []);
  const done = all.filter((i) => i.done).length;
  const pct = all.length ? Math.round((done / all.length) * 100) : 0;

  return (
    <div className="border border-[#EBE5E0] bg-white p-6" data-testid="overview-checklist-summary">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ListChecks size={18} strokeWidth={1.5} className="text-[#A68A6B]" />
          <h4 className="font-serif text-xl text-[#2B2622]">Checklist Progress</h4>
        </div>
        <span data-testid="overview-checklist-overall" className="text-[11px] uppercase tracking-wider text-[#A68A6B]">
          {done}/{all.length} · {pct}%
        </span>
      </div>
      <div className="h-1.5 bg-[#F2EFEB] mb-5">
        <div className="h-full bg-[#A68A6B] transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-3">
        {lists.map((l) => {
          const items = l.items || [];
          const d = items.filter((i) => i.done).length;
          const p = items.length ? Math.round((d / items.length) * 100) : 0;
          return (
            <div key={l.id} data-testid={`overview-checklist-${l.id}`}>
              <div className="flex justify-between text-[11px] text-[#5C5651] mb-1">
                <span className="text-[#2B2622]">{l.name}</span>
                <span>{d}/{items.length}</span>
              </div>
              <div className="h-1 bg-[#F2EFEB]">
                <div className="h-full bg-[#C9B79E] transition-all" style={{ width: `${p}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function OverviewTab({ project, readOnly, onUpdate }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: project.name,
    description: project.description || "",
    status: project.status,
    progress: project.progress || 0,
    budget_total: project.budget_total || 0,
    client_email: project.client_email || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${project.id}`, {
        ...form,
        progress: Number(form.progress),
        budget_total: Number(form.budget_total),
        client_email: form.client_email || null,
      });
      toast.success("Project updated");
      onUpdate();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (readOnly) {
    return (
      <div className="grid sm:grid-cols-2 gap-px bg-[#EBE5E0] border border-[#EBE5E0] max-w-3xl" data-testid="overview-readonly">
        {[
          ["Phase", project.status],
          ["Budget", `$${(project.budget_total || 0).toLocaleString()}`],
          ["Designer", project.designer_name || "—"],
          ["Client", project.client_name || project.client_email || "—"],
        ].map(([k, v]) => (
          <div key={k} className="bg-white p-6">
            <p className="text-[11px] uppercase tracking-wider text-[#A68A6B]">{k}</p>
            <p className="font-serif text-2xl text-[#2B2622] mt-1">{v}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="overview-edit">
      {user?.role === "designer" && <ImportReviewSection project={project} onUpdate={onUpdate} />}
      {user?.role === "designer" && <ChecklistSummary projectId={project.id} />}
      <div>
        <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Project name</Label>
        <Input data-testid="overview-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Description</Label>
        <Textarea data-testid="overview-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
      </div>
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="overview-status" className="mt-1 rounded-none border-[#EBE5E0] focus:ring-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Budget ($)</Label>
          <Input data-testid="overview-budget" type="number" value={form.budget_total}
            onChange={(e) => setForm({ ...form, budget_total: e.target.value })}
            className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-[#5C5651]">Client email</Label>
        <Input data-testid="overview-client" type="email" value={form.client_email}
          onChange={(e) => setForm({ ...form, client_email: e.target.value })}
          className="mt-1 rounded-none border-[#EBE5E0] focus-visible:ring-0 focus-visible:border-[#A68A6B]" />
      </div>
      <Button data-testid="overview-save" onClick={save} disabled={busy}
        className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs py-6 px-8">
        {busy ? "Saving…" : "Save Changes"}
      </Button>
    </div>
  );
}
